// Now and then, a one-line tip about something people miss now that the
// controls panel folds into groups: Astronomy mode, the Milky Way, the black
// holes, the wormhole, sky events, deep space, To Scale. Built to stay out of
// the way:
//   - the first only after TIP_FIRST_MS on the page, once the person has
//     touched the scene and then left it alone for a few seconds
//   - at most TIPS_PER_DAY a day, TIP_GAP_MS apart, each tip once ever,
//     and none for a feature the person has already used
//   - only while canShow() holds (no flight, show, photo mode, open panel)
//   - one nudge a session (src/ui/nudges.js): a tip, the wallpaper card or
//     the sky alerts card, never two, and none while the sky alerts card is
//     due
//   - it slides away by itself after TIP_SHOW_MS, and "No more tips" ends
//     them for good
//
//   startTips({ canShow: () => bool, left: () => px | null,
//               actions: { focus(key), scene(id), openGroup(group, id), click(id), point(id) } })

import { IS_APP } from '../appmode.js';
import { track } from '../analytics.js';
import { markNudge, nudgeFree } from './nudges.js';

// A Mac at a desk: the screensaver is Mac-only, and an iPad reports itself
// as a Macintosh but has touch points.
const IS_MAC_DESKTOP = typeof navigator !== 'undefined'
  && ((navigator.userAgentData && navigator.userAgentData.platform === 'macOS')
    || (/Macintosh/.test(navigator.userAgent) && (navigator.maxTouchPoints || 0) < 2));

const KEY = 'ui.tips.v1';
// ?tips=now skips the wait (and forgets the tips already seen), for checking them.
const NOW = typeof location !== 'undefined' && new URLSearchParams(location.search).get('tips') === 'now';
const TIP_FIRST_MS = NOW ? 2000 : 75000;
const TIP_GAP_MS = 5 * 60000;
const TIP_SHOW_MS = 14000;
const TIPS_PER_DAY = 2;
const IDLE_MS = 4000;

const TIPS = [
  // First: Astronomy mode is the free planetarium, and the way into Pro.
  { id: 'astronomy', text: 'See the real sky above you: Astronomy mode shows the stars, the planets and the Moon from where you are, right now.', label: 'Show me', uses: 'astronomy-toggle', run: (a) => a.click('astronomy-toggle') },
  // The one tip that sells something, second so a first visit can see it:
  // Mac desktops only, and never in the App Store apps (no outside
  // purchases there) or for someone who has opened the screensaver page.
  { id: 'screensaver', text: 'This view can be your Mac\'s screensaver: the real solar system, live and at true scale, for $1.99.', label: 'See the screensaver', uses: 'screensaver-link', when: () => IS_MAC_DESKTOP && !IS_APP, run: () => window.open('/screensaver/', '_blank', 'noopener') },
  { id: 'music', text: 'The music comes in two kinds: Space Drone, made as you listen, or Quiet Wonder, a recorded track. Switch between them under the Ambient button.', label: 'Show me', uses: 'music-pick', run: (a) => a.point('music-pick') },
  { id: 'galaxy', text: 'The Milky Way is in here too. Head out past the planets to see our galaxy from the outside.', label: 'Show me', run: (a) => a.focus('milky-way') },
  { id: 'blackhole', text: 'There is a black hole to visit, its disc bending the starlight around it.', label: 'Take me there', run: (a) => a.focus('black hole') },
  { id: 'satellites', text: 'Switch on Satellites under Explore to see Starlink and 16,000 others where they are right now.', label: 'Show me', uses: 'satellites-toggle', run: (a) => a.openGroup('explore', 'satellites-toggle') },
  { id: 'events', text: IS_APP ? 'Eclipses and meteor showers coming up are under Explore, Events.' : 'Eclipses and meteor showers coming up are under Explore, Events, with an email reminder if you want one.', label: 'Open Events', uses: 'space-events-toggle', run: (a) => a.click('space-events-toggle') },
  { id: 'deep-space', text: 'Switch on Deep space under Explore to fill the sky with nebulae and other galaxies.', label: 'Show me', uses: 'deep-space-toggle', run: (a) => a.openGroup('explore', 'deep-space-toggle') },
  { id: 'wormhole', text: 'A wormhole near Saturn leads to Gargantua, a supermassive black hole in another system.', label: 'Show me', run: (a) => a.scene('wormhole-approach') },
  { id: 'scale', text: 'See every planet side by side at its true size under Shows, To Scale.', label: 'Show me', uses: 'scale-toggle', run: (a) => a.openGroup('shows', 'scale-toggle') },
  { id: 'objects', text: 'Every moon, comet and spacecraft is searchable in Explore, Objects.', label: 'Show me', uses: 'object-browser-toggle', run: (a) => a.openGroup('explore', 'object-browser-toggle') },
  { id: 'fly', text: 'Take the controls: Fly mode lets you pilot a ship between the worlds.', label: 'Show me', uses: 'fly-toggle', run: (a) => a.point('fly-toggle') },
];

const CSS = `
.vt-tip { position: fixed; left: 18px; bottom: 72px; z-index: 60; width: min(340px, calc(100vw - 36px)); box-sizing: border-box;
  padding: 14px 44px 12px 16px; background: #0a0d13; border: none; border-radius: 12px;
  color: #ece7da; font: 13px/1.5 Inter, -apple-system, sans-serif;
  opacity: 0; transform: translateY(8px); transition: opacity .35s ease, transform .35s ease; }
.vt-tip.on { opacity: 1; transform: none; }
.vt-tip-kicker { display: block; margin-bottom: 4px; font-size: 12px; font-weight: 500; color: #7dd3a8; }
.vt-tip-actions { display: flex; align-items: center; gap: 14px; margin-top: 12px; }
.vt-tip-go { font: inherit; font-size: 12px; font-weight: 500; color: #ece7da; background: #15302a;
  border: none; border-radius: 8px; min-height: 36px; padding: 0 12px; cursor: pointer; transition: background-color .15s ease; }
.vt-tip-go:hover { background: #1b3b33; }
.vt-tip-off { font: inherit; font-size: 12px; color: #a8a396; background: none; border: 0; min-height: 36px; padding: 0; cursor: pointer; }
.vt-tip-off:hover { color: #ece7da; }
.vt-tip-close { position: absolute; top: 6px; right: 6px; width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center;
  background: none; border: 0; border-radius: 8px; color: #a8a396; font-size: 20px; line-height: 1; cursor: pointer; padding: 0; }
.vt-tip-close:hover { color: #ece7da; background: #141922; }
.vt-tip-go:focus-visible, .vt-tip-off:focus-visible, .vt-tip-close:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
.tip-pulse { outline: 2px solid transparent; outline-offset: 2px; animation: tip-pulse 0.8s ease-in-out 3; }
@keyframes tip-pulse { 50% { outline-color: #7dd3a8; } }
@media (prefers-reduced-motion: reduce) { .vt-tip { transition: none; } .tip-pulse { animation: none; outline-color: #7dd3a8; } }
@media (max-width: 640px) { .vt-tip { bottom: 120px; } }
`;

function read() {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch { return {}; }
}
function write(state) {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* private window: no tips remembered, and none repeat this session */ }
}

export function startTips({ canShow, actions, left = () => null }) {
  const state = NOW ? {} : read();
  if (state.off) return { stop() {} };
  state.seen = state.seen || [];
  state.used = state.used || [];
  state.days = state.days || {};

  // A feature someone has used needs no tip.
  for (const t of TIPS) {
    if (!t.uses) continue;
    const el = document.getElementById(t.uses);
    if (el) el.addEventListener('click', () => { if (!state.used.includes(t.id)) { state.used.push(t.id); write(state); } }, { passive: true });
  }

  const started = Date.now();
  let touched = false;
  let lastInput = 0;
  let lastShown = state.lastAt || 0;
  let shownThisSession = 0;
  let calmSince = 0;
  const note = () => { touched = true; lastInput = Date.now(); };
  for (const ev of ['pointerdown', 'wheel', 'keydown', 'touchstart']) window.addEventListener(ev, note, { passive: true, capture: true });

  const today = () => new Date().toISOString().slice(0, 10);
  function next() {
    return TIPS.find((t) => !state.seen.includes(t.id) && !state.used.includes(t.id) && (!t.when || t.when()));
  }

  let card = null;
  function show(tip) {
    if (!document.getElementById('vt-tip-css')) {
      const style = document.createElement('style');
      style.id = 'vt-tip-css';
      style.textContent = CSS;
      document.head.appendChild(style);
    }
    state.seen.push(tip.id);
    if (!NOW) markNudge('tips');
    track('tip_view', { tip: tip.id });
    state.lastAt = lastShown = Date.now();
    state.days = { [today()]: (state.days[today()] || 0) + 1 };
    write(state);
    shownThisSession++;

    card = document.createElement('div');
    card.className = 'vt-tip';
    card.setAttribute('role', 'status');
    card.innerHTML = '<span class="vt-tip-kicker">Tip</span><span class="vt-tip-text"></span><div class="vt-tip-actions"><button type="button" class="vt-tip-go"></button><button type="button" class="vt-tip-off">No more tips</button></div><button type="button" class="vt-tip-close" aria-label="Close tip">×</button>';
    card.querySelector('.vt-tip-text').textContent = tip.text;
    card.querySelector('.vt-tip-go').textContent = tip.label;
    const self = card;
    let timer = null;
    const hide = () => {
      clearTimeout(timer);
      self.classList.remove('on');
      setTimeout(() => self.remove(), 400);
      if (card === self) card = null;
    };
    const arm = () => { clearTimeout(timer); timer = setTimeout(hide, TIP_SHOW_MS); };
    self.addEventListener('mouseenter', () => clearTimeout(timer));
    self.addEventListener('mouseleave', arm);
    self.querySelector('.vt-tip-go').addEventListener('click', () => { hide(); track('tip_click', { tip: tip.id }); try { tip.run(actions); } catch (err) { console.warn('[tips]', err); } });
    self.querySelector('.vt-tip-off').addEventListener('click', () => { state.off = true; write(state); hide(); });
    self.querySelector('.vt-tip-close').addEventListener('click', hide);
    const x = left();
    if (x != null) self.style.left = `${x}px`;
    document.body.appendChild(self);
    requestAnimationFrame(() => self.classList.add('on'));
    arm();
  }

  const timer = setInterval(() => {
    if (card || document.hidden) { calmSince = 0; return; }
    const now = Date.now();
    const ready = touched
      && now - started > TIP_FIRST_MS
      && (NOW || now - lastShown > TIP_GAP_MS)
      && now - lastInput > (NOW ? 500 : IDLE_MS)
      && (state.days[today()] || 0) < TIPS_PER_DAY
      && shownThisSession < TIPS_PER_DAY
      && (NOW || nudgeFree('tips'))
      && canShow();
    // The calm has to last a few seconds, so a tip never lands mid-gesture.
    if (!ready) { calmSince = 0; return; }
    if (!calmSince) { calmSince = now; return; }
    if (now - calmSince < 3000) return;
    const tip = next();
    if (!tip) { clearInterval(timer); return; }
    calmSince = 0;
    show(tip);
  }, 1000);

  // For checks from the console or a test: why no tip is showing.
  window.__tips = {
    state,
    why: () => ({ touched, sinceStart: Date.now() - started, sinceLast: Date.now() - lastShown, sinceInput: Date.now() - lastInput,
      today: state.days[today()] || 0, session: shownThisSession, nudgeFree: nudgeFree('tips'), canShow: canShow(), hidden: document.hidden, next: next() && next().id }),
  };
  return { stop() { clearInterval(timer); if (card) card.remove(); } };
}
